import React from 'react';
import styled from 'styled-components';
import { Button, Col, Empty, Modal, Row, Tag, Tooltip } from 'antd';
import {
  BookOutlined,
  CameraOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  PlusOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { countItems, countPhotoItems, describeSchedule } from 'src/constants/checklists';

const Head = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6em;
  margin-bottom: 1.25em;

  h2 {
    font-family: Nunito, sans-serif;
    font-weight: 800;
    font-size: 18px;
    margin: 0;
    flex: 1 1 10em;
  }
`;

const Department = styled.h3`
  font-family: Nunito, sans-serif;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #5b6660;
  margin: 1.5em 0 0.6em;
  &:first-of-type {
    margin-top: 0;
  }
`;

// A div rather than a <button>, because it contains the Duplicate button and
// buttons can't nest. Keyboard support is added where it's rendered.
const TemplateCard = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.45em;
  width: 100%;
  height: 100%;
  padding: 1em 1.1em;
  background: #fff;
  border: 1px solid #e3ebe5;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;

  &:hover {
    border-color: #9fd3ae;
    box-shadow: 0 6px 18px rgba(31, 61, 38, 0.08);
    transform: translateY(-1px);
  }
  &:focus-visible {
    outline: 2px solid #2f8a4c;
    outline-offset: 2px;
  }
  &.inactive {
    opacity: 0.6;
  }

  .top {
    display: flex;
    align-items: flex-start;
    gap: 0.5em;
  }
  .name {
    flex: 1;
    font-weight: 700;
    font-size: 15px;
    line-height: 1.3;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25em 0.9em;
    color: #5b6660;
    font-size: 12.5px;
  }
  .meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35em;
  }
`;

const LibraryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1em;
  padding: 0.85em 0;
  border-top: 1px solid #eef1ef;

  .text {
    flex: 1;
    min-width: 0;
  }
  .name {
    font-weight: 700;
  }
  .sub {
    color: #5b6660;
    font-size: 12.5px;
  }
`;

const FREQUENCY_LABEL = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  onDemand: 'On demand'
};

const TemplateList = ({
  templates = [],
  library = [],
  onEdit,
  onCreate,
  onDuplicate,
  onAddFromLibrary
}) => {
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const [adding, setAdding] = React.useState(null);

  const groups = templates.reduce((acc, t) => {
    const key = t.department || 'Other';
    (acc[key] = acc[key] || []).push(t);
    return acc;
  }, {});
  const departments = Object.keys(groups).sort();

  const addedKeys = new Set(templates.map(t => t.libraryKey).filter(Boolean));

  const add = async key => {
    setAdding(key);
    try {
      await onAddFromLibrary(key);
    } finally {
      setAdding(null);
    }
  };

  const libraryButton = (
    <Button icon={<BookOutlined />} onClick={() => setLibraryOpen(true)}>
      Add from library
    </Button>
  );
  const newButton = (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      onClick={onCreate}
      style={{ backgroundColor: '#1c1c1c' }}
    >
      New checklist
    </Button>
  );

  return (
    <>
      <Head>
        <h2>
          Templates{' '}
          <span style={{ color: '#7a857f', fontWeight: 600 }}>{templates.length}</span>
        </h2>
        {libraryButton}
        {newButton}
      </Head>

      {!templates.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No checklists for this site yet. Start from the library, or build one from scratch."
          style={{ padding: '3em 0' }}
        >
          <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'center', flexWrap: 'wrap' }}>
            {libraryButton}
            {newButton}
          </div>
        </Empty>
      ) : (
        departments.map(department => (
          <React.Fragment key={department}>
            <Department>{department}</Department>
            <Row gutter={[12, 12]}>
              {groups[department].map(t => {
                const items = countItems(t);
                const photos = countPhotoItems(t);
                return (
                  <Col xs={24} sm={12} xl={8} key={t._id}>
                    <TemplateCard
                      role="button"
                      tabIndex={0}
                      className={t.active === false ? 'inactive' : ''}
                      onClick={() => onEdit(t)}
                      onKeyDown={e => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onEdit(t);
                        }
                      }}
                    >
                      <div className="top">
                        <span className="name">{t.name}</span>
                        <Tooltip title="Duplicate">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={e => {
                              e.stopPropagation();
                              onDuplicate(t);
                            }}
                          />
                        </Tooltip>
                      </div>
                      <div className="meta">
                        <span>
                          <ClockCircleOutlined /> {describeSchedule(t.schedule)}
                        </span>
                      </div>
                      <div className="meta">
                        <span>
                          <UnorderedListOutlined /> {items} item{items === 1 ? '' : 's'}
                        </span>
                        {photos > 0 && (
                          <span>
                            <CameraOutlined /> {photos} photo{photos === 1 ? '' : 's'}
                          </span>
                        )}
                        {t.active === false && <Tag style={{ margin: 0 }}>Inactive</Tag>}
                      </div>
                    </TemplateCard>
                  </Col>
                );
              })}
            </Row>
          </React.Fragment>
        ))
      )}

      <Modal
        open={libraryOpen}
        title="Checklist library"
        footer={null}
        onCancel={() => setLibraryOpen(false)}
        width={640}
      >
        <p style={{ color: '#5b6660', marginBottom: '0.5em' }}>
          Ready-made checklists. Adding one gives this site its own copy to edit.
        </p>
        {library.map(entry => {
          const added = addedKeys.has(entry.libraryKey);
          return (
            <LibraryRow key={entry.libraryKey}>
              <div className="text">
                <div className="name">{entry.name}</div>
                <div className="sub">
                  {entry.department} · {FREQUENCY_LABEL[entry.frequency] || entry.frequency} ·{' '}
                  {entry.itemCount} items
                </div>
                {entry.description && <div className="sub">{entry.description}</div>}
              </div>
              {added && <Tag color="green">Added</Tag>}
              <Button
                type={added ? 'default' : 'primary'}
                loading={adding === entry.libraryKey}
                onClick={() => add(entry.libraryKey)}
                style={added ? undefined : { backgroundColor: '#1c1c1c' }}
              >
                {added ? 'Add again' : 'Add'}
              </Button>
            </LibraryRow>
          );
        })}
      </Modal>
    </>
  );
};

export default TemplateList;
